import styled from 'styled-components';

import { IconButton } from '../../../cdl/IconButton/IconButton';
import { Tag } from '../../../cdl/Tag/Tag';
import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../../common/helpers/translate.helper';
import { IconArrowRight } from '../../../common/icons/cdl/ArrowRight';
import { IconPencil } from '../../../common/icons/cdl/Pencil';
import { IconTrash } from '../../../common/icons/cdl/Trash';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';

import { formatPriceListDate } from './priceListValidation.helpers';
import { PriceListFormValue } from './types';

const DownloadableFileName = styled.button`
    padding: 0;
    border: 0;
    background: transparent;
    color: ${theme.colors.accent.foreground};
    font: inherit;
    font-weight: ${theme.fontWeights[600]};
    text-align: left;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }

    &:active {
        opacity: 0.8;
    }
`;

interface PriceListEntryProps {
    priceList: PriceListFormValue;
    onEdit: () => void;
    onDelete: () => void;
    onDownload?: () => void;
    faded?: boolean;
}

export const PriceListEntry = ({ priceList, onEdit, onDelete, onDownload, faded = false }: PriceListEntryProps) => {
    const startDateLabel = priceList.validFrom ? formatPriceListDate(priceList.validFrom) : '—';
    const endDateLabel = priceList.validUntil
        ? formatPriceListDate(priceList.validUntil)
        : translate('contracts.priceLists.endOfContract');

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            style={faded ? { opacity: 0.2, pointerEvents: 'none' } : undefined}
        >
            <Box display="flex" flexDirection="column" gap={4}>
                <Box display="flex" alignItems="center" gap={6}>
                    <Text variant="fieldLabel">{startDateLabel}</Text>
                    <IconArrowRight width={14} height={14} />
                    <Text variant="fieldLabel">{endDateLabel}</Text>
                    {priceList.activeOnApproval ? (
                        <Tag variant="accent">{translate('contracts.priceLists.status.active')}</Tag>
                    ) : null}
                </Box>
                {onDownload ? (
                    <DownloadableFileName type="button" onClick={onDownload}>
                        {priceList.fileName}
                    </DownloadableFileName>
                ) : (
                    <Text variant="subtitle2">{priceList.fileName}</Text>
                )}
            </Box>
            <Box display="flex" gap={4}>
                <IconButton
                    Icon={IconTrash}
                    emphasis="transparent"
                    size="medium"
                    type="button"
                    onClick={onDelete}
                    tooltipLabel={translate('contracts.priceLists.delete')}
                />
                <IconButton
                    Icon={IconPencil}
                    emphasis="transparent"
                    size="medium"
                    type="button"
                    onClick={onEdit}
                    tooltipLabel={translate('contracts.priceLists.edit')}
                />
            </Box>
        </Box>
    );
};
